import {FC} from "react";
import {useTranslation} from "react-i18next";
import {useFlags} from "launchdarkly-react-client-sdk";
import {Alert} from "@interstate/components/Alert";
import {useSidebarItems, useNavigation} from "../../../navigation";
import {PageHeading, PageTemplate} from "../../../layout";
import {CreateEntityBuilder} from "./CreateEntityBuilder";
import {useFeedback} from "../../../feedback";
import {EntitySourceType} from "../../EntitySourceType";
import "./CreateNewEntityPage.scss";
import {features} from "../../../features";

/**
 * The {@link CreateNewEntityPage} provides a 3-step stepper workflow for creating
 * new test operational entities. This integrates the CreateNewEntityStepper component
 * which handles the form, review, and results steps.
 */
export const CreateNewEntityPage: FC = () => {
  const {t} = useTranslation();
  const flags = useFlags();
  const sidebarItems = useSidebarItems({page: "create-new-entity"});
  const {navigateToEntityFromEvent, navigateTo} = useNavigation();
  const [, setFeedback] = useFeedback();

  /**
   * Handles successful entity creation by showing success notification with navigation option
   */
  const handleSuccess = (entityId: string): void => {
    setFeedback({
      type: "success",
      autoHide: false,
      message: t(
        "create-new-entity-page.feedback.success",
        "Test Operational Entity Created"
      ),
      action: {
        label: t("create-new-entity-page.feedback.view-entity", "View Entity"),
        onClick: event => {
          navigateToEntityFromEvent(
            event,
            entityId,
            EntitySourceType.OPERATIONAL
          );
          // Prevent the onClose handler from firing when action is clicked
          event.preventDefault();
        }
      },
      onClose: event => {
        // Only clear feedback if this wasn't triggered by action button click
        if (!event.defaultPrevented) {
          setFeedback(undefined);
        }
      }
    });
  };

  const handleCancel = () => {
    navigateTo(null, "/");
  };

  // Check if the create new entity feature flag is enabled
  const isCreateNewEntityEnabled =
    flags[features.dev.entities.enableCreateNewEntity];

  return (
    <PageTemplate
      id={"create-new-entity-page"}
      title={"Create Test Entity Page"}
      banner={
        <PageHeading
          title={t("create-new-entity-page.title", "Create Test Entity")}
          summary={t(
            "create-new-entity-page.summary",
            "Create a new operational entity using a 3-step workflow"
          )}
        />
      }
      sidebarItems={sidebarItems}>
      {isCreateNewEntityEnabled ? (
        <CreateEntityBuilder />
      ) : (
        <Alert type="caution">
          <p>
            {t(
              "create-new-entity-page.access-denied",
              "You don't have access to this page."
            )}
          </p>
        </Alert>
      )}
    </PageTemplate>
  );
};
